/**
 * VueX store for room members.
 *
 * Loads users in batches (groups). Short lists of 50 or fewer users are loaded in one go to not do a lot of requests
 * for small groups.
 *
 * How it works:
 *
 * - it is initialized with a room ID and a map of "user group key" => "user count". The "group key" currently is the
 *   first letter of the user's first name
 * - loading a group of users can be triggered via the fetchUsers() action
 *   - once the users are loaded, they are also available via the getter "users"
 * - while the loading of a group is in progress, there is a promise available in loadingPromises(). Once the group is
 *   loaded, this promise is automatically fulfilled
 */

import { ActionTree, MutationTree } from 'vuex';
import { Actions, AugmentedActionContext, Getters, Mutations, MutationTypes, State } from './types/room-members.types';
import Api from '../../scripts/api';
import { Profile } from '../../scripts/api/models/user';
import UserList from './room-members/UserList';

type UsersByKey = { [key: string]: Profile[] };

const state: State = {
    // required for initializing the component
    userCountByGroup: null,

    // required for initializing the component
    roomUid: null,

    // the actual list of users
    usersByGroup: new Map(),

    // keys that are already loaded
    loadedKeys: [],

    // keys that are currently being loaded
    loadingKeys: [],

    // keys whose last load attempt failed
    failedKeys: [],

    // Promises for groups that are currently being loaded
    loadingPromises: new Map(),

    // resolve functions for loadingPromises
    loadingPromiseResolvers: new Map(),
};

const getters: Getters = {
    groupsAvailable (state: State): boolean {
        return state.userCountByGroup !== null;
    },

    groupKeys (state: State): Iterable<string> {
        return Array.from(state.userCountByGroup?.keys() ?? []);
    },

    userListLoaded (state: State): (key: string) => boolean {
        return (key: string) => state.loadedKeys.indexOf(key) !== -1;
    },

    userListFailed (state: State): (key: string) => boolean {
        return (key: string) => state.failedKeys.indexOf(key) !== -1;
    },

    groupsAndCount (state: State): null | Map<string, number> {
        return state.userCountByGroup;
    },

    users (state: State): Map<string, UserList> {
        return state.usersByGroup;
    },
};

const actions: ActionTree<State, State> & Actions = {
    setRoomUid ({ commit }: AugmentedActionContext, roomUid: number) {
        commit(MutationTypes.SET_ROOM_UID, roomUid);
    },

    setGroups ({ commit } : AugmentedActionContext, groups: { [key: string]: number }): void {
        const groupsMap: Map<string, number> = new Map(Object.entries(groups));
        commit(MutationTypes.INITIALIZE_GROUPS, groupsMap);
    },

    async fetchUsers ({ commit }: AugmentedActionContext, key: string): Promise<Profile[]> {
        const totalUsersCount: number = Array.from(state.userCountByGroup?.values() ?? [])
            .reduce((sum: number, count: number) => sum + count, 0);

        const keyToLoad: string = totalUsersCount <= 50 ? '*' : key;

        const keyIsAlreadyLoaded = state.loadedKeys.indexOf(key) > -1;
        const keyIsLoading = state.loadingKeys.indexOf(keyToLoad) > -1;

        const load = !keyIsAlreadyLoaded && !keyIsLoading;

        const promise = state.loadingPromises.get(key);
        if (keyIsLoading && promise) {
            return promise;
        }

        if (load) {
            commit(MutationTypes.START_LOADING, keyToLoad);

            // the keys this request is responsible for: the bulk request covers every known group
            const requestedKeys: string[] = keyToLoad === '*'
                ? Array.from(state.userCountByGroup?.keys() ?? [])
                : [key];

            try {
                if (keyToLoad === '*') {
                    // fetching all users and then manually sorting into the correct sections
                    const users: Profile[] = await Api.version(2).get(`rooms/${state.roomUid}/members`);

                    if (users === undefined) {
                        throw new Error('Received no users');
                    }

                    const groupedUsers: UsersByKey = {};
                    users.forEach((user: Profile) =>
                        groupedUsers[user.listKey]?.push(user) ?? (groupedUsers[user.listKey] = [user])
                    );

                    // Commit every known group, even the ones the response had no users for: a group that stays
                    // uncommitted is indistinguishable from "still loading" and would show skeletons forever.
                    // Group counts come from the database while the users come from Solr, so the response is
                    // allowed to contain fewer users than the counts promised.
                    requestedKeys.forEach(
                        (groupKey: string) => commit(MutationTypes.SET_USERS, {
                            key: groupKey,
                            users: groupedUsers[groupKey] ?? [],
                        })
                    );

                    // users whose list key is not one of the known groups (e.g. an unset key in the Solr document)
                    // cannot be displayed; SET_USERS reports them so the mismatch is visible
                    Object.keys(groupedUsers)
                        .filter((groupKey: string) => requestedKeys.indexOf(groupKey) === -1)
                        .forEach((groupKey: string) => commit(MutationTypes.SET_USERS, {
                            key: groupKey,
                            users: groupedUsers[groupKey],
                        }));
                } else {
                    const users: Profile[] = await Api.version(2).get(`rooms/${state.roomUid}/members?key=${key}`);

                    if (users === undefined) {
                        throw new Error('Received no users');
                    }
                    commit(MutationTypes.SET_USERS, { key, users });
                }
            } catch (error) {
                // Without this the keys stay out of loadedKeys forever, which renders as permanently blurred
                // skeleton items instead of an error the user can act on.
                console.error(`Loading room members for key "${keyToLoad}" failed`, error);
                commit(MutationTypes.LOADING_FAILED, requestedKeys);

                return [];
            } finally {
                commit(MutationTypes.FINISH_LOADING, keyToLoad);
            }
        }

        return state.usersByGroup.get(key)?.users ?? [];
    },
    async removeUser ({ commit }: AugmentedActionContext, user: number): Promise<void> {
        const result = await Api.version(2).delete(`rooms/${state.roomUid}/members/${user}`);

        if (!result || result.success !== true) {
            throw new Error('User remove failed');
        }

        commit(MutationTypes.DELETE_USER, user);
    },
};

const mutations: MutationTree<State> & Mutations = {
    SET_ROOM_UID (state: State, roomUid: number) {
        state.roomUid = roomUid;
    },

    INITIALIZE_GROUPS (state: State, groups: Map<string, number>) {
        state.userCountByGroup = groups;
        const usersByGroup: Map<string, UserList> =
            Array.from(groups.entries())
                .reduce<Map<string, UserList>>(
                    (acc: Map<string, UserList>, [key, userCount]: [string, number]) => acc.set(key, new UserList(userCount)),
                    new Map()
                );
        state.usersByGroup = usersByGroup;
        state.loadedKeys = [];
        state.failedKeys = [];
        state.loadingKeys = [];
        state.loadingPromises = new Map();
        state.loadingPromiseResolvers = new Map();
    },

    START_LOADING (state: State, key: string) {
        if (key === '*') {
            // push a promise + resolver for each key that we need
            Array.from(state.userCountByGroup?.keys() ?? []).forEach((key_: string) => {
                const promise = new Promise<Profile[]>(resolve => {
                    state.loadingPromiseResolvers.set(key_, resolve);
                });
                state.loadingPromises.set(key_, promise);
            });
        } else {
            // we started loading one specific key => make sure to resolve that
            const promise = new Promise<Profile[]>(resolve => {
                state.loadingPromiseResolvers.set(key, resolve);
            });
            state.loadingPromises.set(key, promise);
        }

        state.loadingKeys.push(key);

        // a retry of a previously failed group must not keep rendering the error state
        const keysBeingLoaded = key === '*' ? Array.from(state.userCountByGroup?.keys() ?? []) : [key];
        state.failedKeys = state.failedKeys.filter((failedKey: string) => keysBeingLoaded.indexOf(failedKey) === -1);
    },

    SET_USERS (state: State, payload: { key: string, users: Profile[] }) {
        const usersEntry = state.usersByGroup.get(payload.key);
        if (usersEntry !== undefined) {
            usersEntry.users = payload.users;
            state.loadedKeys.push(payload.key);

            const resolver = state.loadingPromiseResolvers.get(payload.key);
            if (resolver) {
                resolver(payload.users);
            }
        } else {
            console.error(`No users defined for key ${payload.key}`);
        }
    },

    LOADING_FAILED (state: State, keys: string[]) {
        keys.forEach((key: string) => {
            if (state.failedKeys.indexOf(key) === -1) {
                state.failedKeys.push(key);
            }

            // resolve any pending promise so that callers awaiting this group are not stuck forever
            const resolver = state.loadingPromiseResolvers.get(key);
            if (resolver) {
                resolver([]);
                state.loadingPromiseResolvers.delete(key);
                state.loadingPromises.delete(key);
            }
        });
    },

    FINISH_LOADING (state: State, key: string) {
        const index = state.loadingKeys.indexOf(key);
        if (index > -1) {
            state.loadingKeys.splice(index, 1);
        }
    },
    DELETE_USER (state: State, user: number) {
        const usersByGroup: Map<string, UserList> = new Map(state.usersByGroup);
        usersByGroup.forEach((userList: UserList, key: string) => {
            if (!userList || !Array.isArray(userList.users)) {
                return;
            }
            const filteredList = userList.users.filter((u: Profile) => u.id !== user);
            const newUserList = new UserList((userList as any).userCount ?? filteredList.length);
            newUserList.users = filteredList;

            usersByGroup.set(key, newUserList);
        });
        state.usersByGroup = usersByGroup;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
